import {
  IPullRequest,
  IPullRequestSearch,
  IPRStats,
  IPRStatsRequest,
} from 'interface/pullRequestInterface';
import CommonTable, { HeadCell } from '../common/TableCommon';
import { useEffect, useState } from 'react';
import pullRequestService from 'services/pullRequestService';
import StatsCards from './StatsCards';
import { useAppSelector } from 'hooks/reduxHooks';
import { RootState } from 'redux-setup/store';

const PullRequests = () => {
  const [pullRequestData, setPullRequestData] = useState<IPullRequest[]>([]);
  const [stats, setStats] = useState<IPRStats[]>();
  const [isNextAPIDisabled, setIsNextAPIDisabled] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const limit = 25;

  const organizationId = useAppSelector(
    (state: RootState) => state.organization.id,
  );

  const getPRStats = async () => {
    try {
      const payload: IPRStatsRequest = {
        OrganizationID: organizationId,
      };
      const res = await pullRequestService.getPRStats(payload);
      if (res.data && res.data.Result) {
        setStats(res.data.Result);
        setIsNextAPIDisabled(res.data.Result.length < limit);
      }
    } catch (error) {
      setStats(undefined);
    }
  };

  const searchPullRequest = async () => {
    try {
      const payload: IPullRequestSearch = {
        Page: page - 1,
        Limit: limit,
        OrganizationID: organizationId,
        Descending: false,
        OrderBy: 'Number',
      };
      const res = await pullRequestService.searchPullRequest(payload);
      if (res.data && res.data.Result) {
        setPullRequestData(prev => [...prev, ...res.data.Result]);
        setIsNextAPIDisabled(res.data.Result.length < limit);
      }
    } catch (error) {
      setPullRequestData([]);
    }
  };

  useEffect(() => {
    searchPullRequest();
    getPRStats();
  }, [page]);

  const headCellList: HeadCell<IPullRequest>[] = [
    {
      id: 'number',
      label: 'Number',
      hide: false,
      cellRender: row => {
        return <>{row?.Number}</>;
      },
    },
    {
      id: 'title',
      label: 'Title',
      hide: false,
      cellRender: row => {
        return row?.HTMLURL ? (
          <a href={row.HTMLURL} target="_blank" rel="noopener noreferrer">
            {row.Title}
          </a>
        ) : null;
      },
    },
    // TODO: get user name
    {
      id: 'author',
      label: 'Author',
      hide: false,
      cellRender: row => {
        return <>{row?.ExternalUserID}</>;
      },
    },
    {
      id: 'status',
      label: 'Status',
      hide: false,
      cellRender: row => {
        if (!row) return null;
        return <>{row.Draft ? 'Draft' : row.Merged ? 'Merged' : row.State}</>;
      },
    },
    {
      id: 'merged',
      label: 'Merged',
      hide: false,
      cellRender: row => {
        return <>{row?.Merged ? 'Yes' : 'No'}</>;
      },
    },
    {
      id: 'mergedAt',
      label: 'Merged At',
      hide: false,
      cellRender: row => {
        return (
          <>
            {row?.MergedAt ? new Date(row.MergedAt).toLocaleDateString() : ''}
          </>
        );
      },
    },
    {
      id: 'draft',
      label: 'Draft',
      hide: false,
      cellRender: row => {
        return <>{row?.Draft ? 'Yes' : 'No'}</>;
      },
    },

    {
      id: 'labels',
      label: 'Labels',
      hide: false,
      cellRender: row => {
        return <>{row?.Labels?.join(', ') || ''}</>;
      },
    },
    {
      id: 'htmlUrl',
      label: 'URL',
      hide: false,
      cellRender: row => {
        return row?.HTMLURL ? (
          <a href={row.HTMLURL} target="_blank" rel="noopener noreferrer">
            {row.HTMLURL}
          </a>
        ) : null;
      },
    },
    {
      id: 'repository',
      label: 'Repository',
      hide: false,
      cellRender: row => {
        return <>{row?.HeadRepoFullName}</>;
      },
    },
    {
      id: 'createdAt',
      label: 'Created At',
      hide: false,
      cellRender: row => {
        return (
          <>
            {row?.PRCreatedAt ? new Date(row.PRCreatedAt).toLocaleString() : ''}
          </>
        );
      },
    },
    {
      id: 'updatedAt',
      label: 'Updated At',
      hide: false,
      cellRender: row => {
        return (
          <>
            {row?.PRUpdatedAt ? new Date(row.PRUpdatedAt).toLocaleString() : ''}
          </>
        );
      },
    },
    {
      id: 'closedAt',
      label: 'Closed At',
      hide: false,
      cellRender: row => {
        return (
          <>{row?.ClosedAt ? new Date(row.ClosedAt).toLocaleString() : ''}</>
        );
      },
    },
  ];
  return (
    <>
      <StatsCards stats={stats} />
      <CommonTable<IPullRequest>
        headCellList={headCellList}
        data={pullRequestData}
        isNextAPIDisabled={isNextAPIDisabled}
        onEndReached={() => {
          setPage(prev => prev + 1);
        }}
      />
    </>
  );
};

export default PullRequests;
